import React, { useState } from 'react'
import styled from '@emotion/styled'
import { Radio, Button, Form, Image, message, RadioChangeEvent } from 'antd'
import PartnerScan from 'packages/@slerp/assets/scan-partner.png'
import CustomerScan from 'packages/@slerp/assets/scan-customer.png'
import { useMerchant } from 'hooks/useGlobalContext'
import { useMutation } from '@apollo/client'
import { useSession } from 'packages/@slerp/accounts'
import Loading from 'components/Utils/Loading'
import isEmpty from 'lodash/isEmpty'
import { UPDATE_LOYALTY_SCAN_METHOD } from 'components/Loyalty/LoyaltyQueries'

type FormValuesType = {
  loyalty_scan_method: string
}

const LoyaltySettings = () => {
  const [form] = Form.useForm<FormValuesType>()

  const { merchantData, setMerchantData } = useMerchant()
  const { merchant } = useSession()
  const [scanMethod, setScanMethod] = useState<string>(
    merchantData?.loyalty_scan_method || 'partner'
  )

  const [updateLoyaltyScanMethod] = useMutation(UPDATE_LOYALTY_SCAN_METHOD, {
    fetchPolicy: 'no-cache',
    onCompleted: () => {
      setMerchantData({ ...merchantData, loyalty_scan_method: scanMethod })
      message.destroy()
      message.success('Loyalty scanning method updated!')
    },
    onError: () => {
      message.destroy()
      message.error('Something went wrong!')
    }
  })

  const onFinish = (values: FormValuesType) => {
    updateLoyaltyScanMethod({
      variables: {
        merchantId: merchant?.id,
        loyalty_scan_method: values.loyalty_scan_method
      }
    })
  }

  const handleScanMethodChange = (e: RadioChangeEvent) => {
    setScanMethod(e.target.value)
  }

  if (isEmpty(merchantData) || !merchant) {
    return <Loading />
  }

  const initialValues = {
    loyalty_scan_method: scanMethod
  }

  return (
    <Root>
      <SettingsContainer>
        <Form
          form={form}
          onFinish={onFinish}
          layout='vertical'
          initialValues={initialValues}
        >
          <Section>
            <SectionTitle>Loyalty Scanning Method</SectionTitle>
            <SectionDescription>
              Choose who scans the loyalty QR code during a transaction. This
              setting controls whether the QR code is generated on the customer
              app, or your device.
            </SectionDescription>
            <SectionContent>
              <Form.Item name='loyalty_scan_method' required>
                <Radio.Group
                  style={{ flexDirection: 'column', gap: '12px' }}
                  onChange={handleScanMethodChange}
                >
                  <RadioLabel active={scanMethod === 'partner'}>
                    <Radio value='partner' data-testid='partner-radio' />
                    <div>
                      <RadioTitle>STAFF SCANS LOYALTY</RadioTitle>
                      <RadioDescription>
                        Your staff scans the customer’s loyalty QR code. Choose
                        this setting if your device supports QR code scanning.
                        Compatible with Slerp Partner App.
                      </RadioDescription>
                    </div>
                  </RadioLabel>

                  <RadioLabel active={scanMethod === 'customer'}>
                    <Radio value='customer' data-testid='customer-radio' />
                    <div>
                      <RadioTitle>CUSTOMER SCANS LOYALTY</RadioTitle>
                      <RadioDescription>
                        The customer scans the loyalty QR code. Useful if you
                        have static QR codes displayed in your shop.
                      </RadioDescription>
                    </div>
                  </RadioLabel>
                </Radio.Group>
              </Form.Item>
            </SectionContent>
            <SectionDescription>
              You can change this setting at any time. If you are using a third
              party POS, make sure they support your chosen scan setting.
            </SectionDescription>
          </Section>
          <Actions>
            <Button title='Save' type='primary' htmlType='submit'>
              Save
            </Button>
          </Actions>
        </Form>
      </SettingsContainer>

      <Separator />

      <InfoContainer>
        <ScanMethodInfo>
          <Card>
            <CardTitle>Staff scans loyalty</CardTitle>
            <CardContent>
              <Image preview={false} src={PartnerScan} alt='Staff Scan' />
            </CardContent>
            <CardSubtitle>Staff scans customer’s QR code</CardSubtitle>
            <CardDescription>
              Staff uses POS scanner to scan the loyalty QR code
            </CardDescription>
          </Card>
          <Separator padding />
          <Card>
            <CardTitle>Customer scans loyalty</CardTitle>
            <CardContent>
              <Image preview={false} src={CustomerScan} alt='Customer Scan' />
            </CardContent>
            <CardSubtitle>Customer scans loyalty QR code</CardSubtitle>
            <CardDescription>
              Customer scans the loyalty with your branded app
            </CardDescription>
          </Card>
        </ScanMethodInfo>
      </InfoContainer>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  gap: 30px;
  grid-template-columns: 1fr 1px 1fr;
`

const SettingsContainer = styled.div`
  padding: 8px 12px;
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`

const Section = styled.div`
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f3f2f4;
`

const SectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 500;
  color: #4a4a68;
  margin-bottom: 0;
`

const SectionDescription = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: #77778e;
  margin-bottom: 16px;
`

const SectionContent = styled.div`
  margin: 16px 0;
`

const InfoContainer = styled.div`
  padding: 8px 12px;
`

const ScanMethodInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1px 1fr;

  border: 1px solid #d2d2d9;
  border-radius: 4px;
`

const Separator = styled.div<{ padding?: boolean }>`
  height: calc(100% - ${({ padding }) => (padding ? '32px' : '0')});
  margin: ${({ padding }) => (padding ? '16px 0' : '0')};
  border-left: 1px solid #f3f2f4;
`

const Card = styled.div`
  padding: 16px;
`

const CardTitle = styled.h4`
  font-size: 14px;
  font-weight: 500;
  color: #4a4a68;
  text-align: center;

  padding-bottom: 16px;
  border-bottom: 1px solid #d2d2d9;
`

const CardContent = styled.div`
  margin: 16px 0;
`

const CardSubtitle = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: #4a4a68;
  margin: 0;
`

const CardDescription = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: #77778e;
  margin: 0;
`

const RadioLabel = styled.label<{ active: boolean }>(
  ({ active }) => `
  padding: 8px 12px;
  border: 1px solid ${active ? '#4a4a68' : '#D2D2D9'};
  border-radius: 6px;
  background: ${active ? '#fafafb' : '#ffffff'};

  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
`
)

const RadioTitle = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #4a4a68;
  margin-bottom: 0;
  text-align: left;
`

const RadioDescription = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: #77778e;
  margin-bottom: 0;
  text-align: left;
`

export default LoyaltySettings
